import { FormattedMessage, useIntl } from "react-intl";
import { yupResolver } from "@hookform/resolvers/yup";
import { Form, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { FC } from "react";

import useHandleCustomRtkQueryError from "../../../hooks/useHandleCustomRtkQueryError";
import { useInviteExistingUserMutation } from "../../../api/settingsAPI";
import { useInviteUserValidationSchema } from "./validator";

type TInviteUserProps = {
	show: boolean;
	onHide: () => void;
};

export type TInviteUserSubmitData = {
	email: string;
	role: "OWNER" | "STAFF";
};

const InviteUser: FC<TInviteUserProps> = ({ show, onHide }) => {
	const intl = useIntl();
	const inviteUserValidationSchema = useInviteUserValidationSchema();
	const [customError, setCustomError, handleCustomError] =
		useHandleCustomRtkQueryError();

	const [inviteExistingUser] = useInviteExistingUserMutation();

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<TInviteUserSubmitData>({
		resolver: yupResolver(inviteUserValidationSchema),
	});

	const onSubmit = (data: TInviteUserSubmitData) => {
		inviteExistingUser(data).then((response) => {
			if ("error" in response) {
				handleCustomError(response.error);
			} else {
				setCustomError(null);
				onHide();
				toast.success(
					intl.formatMessage({ id: "userInvitedSuccessfully" })
				);
			}
		});
	};

	return (
		<Modal className="modal-500" centered show={show} onHide={onHide}>
			<Modal.Header className="modal__header" closeButton>
				<Modal.Title className="modal__title">
					<FormattedMessage id="inviteExistingUserCamelCase" />
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className="pt-0">
				<Form onSubmit={handleSubmit(onSubmit)}>
					<Form.Group>
						<Form.Label className="default-input-label">
							<FormattedMessage id="email" />
						</Form.Label>
						<Form.Control
							placeholder={`${intl.formatMessage({
								id: "email",
							})}`}
							className="default-input"
							type="text"
							{...register("email")}
						/>
						{(errors.email ||
							customError ===
								"User with email specified is already connected to current tenant." ||
							customError ===
								"User with email specified does not exist in our system.You can register him and connect to your tenant.") && (
							<p className="mb-0 form-field-error">
								{errors.email ? (
									errors.email.message
								) : customError ===
								  "User with email specified is already connected to current tenant." ? (
									<FormattedMessage id="userWithEmailSpecifiedIsAlreadyConnected" />
								) : (
									intl.formatMessage({
										id: "userWithEmailSpecifiedDoesNotExist",
									})
								)}
							</p>
						)}
					</Form.Group>
					<Form.Group className="mt-2">
						<Form.Label className="default-input-label">
							<FormattedMessage id="userRole" />
						</Form.Label>
						<Form.Select
							defaultValue="STAFF"
							className="default-input"
							{...register("role")}
						>
							<option value="" disabled>
								<FormattedMessage id="selectUserRole" />
							</option>
							<option value="OWNER">
								<FormattedMessage id="owner" />
							</option>
							<option value="STAFF">
								<FormattedMessage id="staff" />
							</option>
						</Form.Select>
						{errors.role && (
							<p className="mb-0 form-field-error">
								{errors.role.message}
							</p>
						)}
					</Form.Group>
					<div className="d-flex justify-content-end mt-3">
						<button
							data-testid="invite-user"
							type="submit"
							className="ice-button w-100"
						>
							<FormattedMessage id="invite" />
						</button>
					</div>
				</Form>
			</Modal.Body>
		</Modal>
	);
};

export default InviteUser;
